import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';
import { convertQueryObjToString } from '../../../utility/urlQuery';
import store from '@store';
import tenantData from '@data';
import tenantUtils from '@utils';
import { convertArrayToQueryString, mapQueryParams } from '../../../utility/utility';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import { t } from 'i18next';
import {
  getUnifiedCreditsUsageProductColor,
  mapConsumptionSummaryRowForUnifiedCreditsUsage,
  mapProductForUnifiedCreditsUsage,
} from '../../common/utils/unifiedCreditsUsageProductLabels';
import {
  enrichQuotaRowWithProductVisuals,
  getQuotaSummarySegmentColor,
} from '../../common/utils/quotaCreditsProductVisualsFallback';

/** Surge credits APIs nest unified Oman portfolio data under bayut, not unified. */
const UNIFIED_CREDITS_PLATFORM_KEY = 'bayut';

const dataMapper = (obj, { isUnifiedCreditsUsage } = {}) => {
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });
  return obj.map((item) => {
    const product = item?.product
      ? isUnifiedCreditsUsage
        ? mapProductForUnifiedCreditsUsage(item.product)
        : item.product
      : item?.product;
    const slug = item?.product?.slug;
    const unifiedColor = isUnifiedCreditsUsage ? getUnifiedCreditsUsageProductColor(slug) : null;
    const hashEntry = productsHash[slug];
    return {
      ...item,
      product,
      icon: hashEntry?.icon,
      iconColor: unifiedColor ?? hashEntry?.iconColor,
      iconProps: {
        ...(hashEntry?.iconProps ?? {}),
        ...(unifiedColor ? { color: unifiedColor } : {}),
      },
      appliedDate: item?.performed_at,
      expiryDate: item?.listing?.expiry_date,
    };
  });
};

const summaryDataMapper = (e) => {
  const isUnifiedCreditsUsage = isUnifiedProfolioEnabled({ featureKey: 'platFormSwitch' });
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });
  const productWise = e?.product_wise ?? e?.consumption_summary ?? [];
  const by_products = productWise.map((item) => {
    const slug = item.slug;
    const visuals = enrichQuotaRowWithProductVisuals(slug, productsHash[slug], { isUnifiedCreditsUsage });
    let row = {
      ...item,
      ...visuals,
      icon: productsHash[item.slug]?.icon,
      iconColor: productsHash[item.slug]?.iconColor,
      used_credits: item.consumed_credits ?? item.consume_credits ?? 0,
    };
    if (isUnifiedCreditsUsage) {
      row = mapConsumptionSummaryRowForUnifiedCreditsUsage(row);
    }
    return row;
  });

  let endPoint = 0;
  let lineChartData = '90deg';
  const totalUsed = e?.used ?? 0;

  let startPoint = 0;
  productWise.forEach((summaryItem) => {
    if (!totalUsed) return;
    endPoint = endPoint + ((summaryItem.consumed_credits ?? summaryItem.consume_credits ?? 0) / totalUsed) * 100;
    const segmentColor = getQuotaSummarySegmentColor(summaryItem.slug, productsHash, { isUnifiedCreditsUsage });
    if (segmentColor) {
      lineChartData += ', ' + segmentColor + ' ' + startPoint + '% ' + endPoint + '%';
    }
    startPoint = endPoint;
  });
  lineChartData = `linear-gradient(${lineChartData})`;
  const totalUsedCredits = by_products.reduce((acc, item) => acc + (item.used_credits ?? 0), 0);

  const chartData = {
    datasets: [
      {
        data: by_products.map((item) => item.used_credits),
        backgroundColor: by_products.map((item) => item.iconColor || 'rgba(0, 0, 0, 0.2)'),
      },
    ],
    total: totalUsedCredits,
    labels: by_products.map((item) =>
      isUnifiedCreditsUsage ? t(item.title) : tenantUtils.getLocalisedString(item, 'title'),
    ),
  };

  const res = {
    details: [
      { label: 'Available', value: e?.available },
      { label: 'Used', value: e?.used },
      { label: 'Total', value: e?.allocated ?? e?.total },
    ],
    by_products: by_products,
    chartData: chartData,
    lineChartData: lineChartData,
  };
  return res;
};

const quotaCreditsApiEndpoints = {
  getCreditDeduction: {
    query: ({ listingId }) => {
      return {
        url: `/api/surge/products/applicable_products?listing_id=${listingId}`,
      };
    },
    transformer: (response) => {
      const products = response?.data?.products;
      if (products?.length) {
        // const applicable_produduct = products?.find((e) => e?.product_id == productId);
        return {
          // [platform]: {
          //   available: 100,
          //   required: applicable_produduct?.credits_required?.['30'],
          //   isZoneArea: false,
          //   zoneFactor: 1,
          //   totalQuota: 100,
          // },
        };
      }
    },
  },
  transferCredits: {
    query: (body) => ({
      url: `/api/surge/credits/transfer`,
      method: 'POST',
      body: tenantPayloads.transferQuotaCreditsPayload(body),
    }),
  },
  getQuotaCreditsForTransfer: {
    query: (query) => ({
      url: `/api/surge/dashboard/qc_summary?${convertQueryObjToString(query)}`,
    }),
    transformer: (response) => tenantTransformers.manageTransferQuotaCreditsTransformer(response),
  },
  transferQuota: {
    query: (body) => ({
      url: `/api/surge/quotas/transfer`,
      method: 'POST',
      body: tenantPayloads.transferQuotaCreditsPayload(body),
    }),
  },
  getQuotaCreditsWidgetData: {
    query: ({ platform_id, ...rest }) => {
      const user = store.getState().app.loginUser.user;

      return {
        url: user?.isCurrencyUser
          ? `/api/surge/credits/summary?${convertQueryObjToString(rest)}&${convertArrayToQueryString(platform_id, 'platform_ids')}`
          : `/api/surge/dashboard/qc_summary?${convertQueryObjToString(rest)}`,
      };
    },
    transformer: (response, combineCredits) => {
      const user = store.getState().app.loginUser.user;
      if (user?.isCurrencyUser) {
        return tenantTransformers.creditsWidgetMapper(response, combineCredits);
      }
    },
  },

  getCustomCreditsPrice: {
    query: (queryParams) => {
      return { url: `/api/surge/packages/calculate_price?${convertQueryObjToString(queryParams)}` };
    },
    transformer: (response) => tenantTransformers.customCreditsPriceDataMapper(response),
  },

  getConsumptionHistoryData: {
    query: ({ page, params, filtersList }) => {
      const mappedParams = mapQueryParams(params, filtersList);
      return { url: `/api/surge/credits/consumption_history?page=${page || 1}&${mappedParams}` };
    },
    transformer: (response, meta, { platforms } = {}) => {
      const data = {};
      const isUnifiedCreditsUsage = isUnifiedProfolioEnabled({ featureKey: 'platFormSwitch' });
      const history = response?.credits_consumption_history;

      if (isUnifiedCreditsUsage) {
        const platformData = history?.[UNIFIED_CREDITS_PLATFORM_KEY];
        if (platformData) {
          data.unified = {
            data: dataMapper(platformData, { isUnifiedCreditsUsage }),
            pagination: response?.pagination,
          };
        }
        return data;
      }

      platforms?.forEach((platform) => {
        const platformSlug = platform?.slug;
        const platformData = history?.[platformSlug];
        if (platformData) {
          data[platformSlug] = {
            data: dataMapper(platformData, { isUnifiedCreditsUsage }),
            pagination: response?.pagination,
          };
        }
      });

      return data;
    },
  },
  getConsumptionSummaryData: {
    query: ({ params } = {}) => {
      const mappedParams = mapQueryParams(params);
      return { url: `/api/surge/credits/consumption_summary${mappedParams ? `?${mappedParams}` : ''}` };
    },
    transformer: (response, meta, { platforms }) => {
      let responses = {};
      const isUnifiedCreditsUsage = isUnifiedProfolioEnabled({ featureKey: 'platFormSwitch' });
      const summary = response?.credits_consumption_summary;

      if (isUnifiedCreditsUsage) {
        const payload = summary?.[UNIFIED_CREDITS_PLATFORM_KEY];
        if (payload) {
          responses.unified = summaryDataMapper(payload);
        }
        return responses;
      }

      platforms?.forEach((platform) => {
        const slug = platform?.slug;
        if (summary?.[slug]) {
          responses[slug] = summaryDataMapper(summary[slug]);
        }
      });
      return responses;
    },
  },
};

export default quotaCreditsApiEndpoints;
